import React, { useRef, useCallback, useEffect } from 'react';

import { Facepile } from 'app/src/components/Facepile';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { forNamespace } from '@trello/i18n';

import PopOver from 'app/scripts/views/lib/pop-over';
import { BoardTableCardMemberSelectView } from 'app/scripts/views/card/board-table-card-member-select-view';

import { MemberAvatar } from 'app/src/components/MemberAvatar';

import { CellWrapper } from './CellWrapper';
import { Members } from './types';
import styles from './MembersCell.less';
import { Analytics } from '@trello/atlassian-analytics';
import { TableTestClasses } from '@trello/test-ids';

const viewTitle = forNamespace('view title');

enum Screen {
  MemberScreen,
}

interface MembersCellProps {
  idCard: string;
  idBoard: string;
  idOrg: string;
  avatarSize?: number;
  memberIds: string[];
  members: (Members[0] & { deactivated?: boolean })[];
  editable?: boolean;
}

export const MembersCell: React.FunctionComponent<MembersCellProps> = React.memo(
  ({
    avatarSize = 24,
    memberIds,
    members,
    idCard,
    idBoard,
    editable,
    idOrg,
  }) => {
    const { toggle, popoverProps, triggerRef } = usePopover<HTMLDivElement>({
      initialScreen: Screen.MemberScreen,
    });

    useEffect(() => {
      if (popoverProps.isVisible) {
        Analytics.sendScreenEvent({
          name: 'cardMemberListInlineDialog',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });
      }
    }, [popoverProps.isVisible, idOrg]);

    const classicTriggerRef = useRef<HTMLDivElement>(null);

    const toggleMemberSelectPopover = useCallback(() => {
      if (editable) {
        if (!PopOver.isVisible) {
          Analytics.sendClickedButtonEvent({
            source: 'multiBoardTableViewScreen',
            buttonName: 'changeMembersButton',
            containers: {
              organization: {
                id: idOrg,
              },
            },
          });
        }
        PopOver.toggle({
          elem: classicTriggerRef.current,
          view: BoardTableCardMemberSelectView,
          options: {
            idCard,
            idBoard,
          },
        });
      } else {
        if (!popoverProps.isVisible) {
          Analytics.sendClickedButtonEvent({
            source: 'multiBoardTableViewScreen',
            buttonName: 'memberListButton',
            containers: {
              organization: {
                id: idOrg,
              },
            },
          });
        }
        toggle();
      }
    }, [editable, idCard, idBoard, idOrg, popoverProps.isVisible, toggle]);

    const avatars = members.reverse().map((member) => {
      return (
        <div
          key={member.id}
          className={styles.avatar}
          data-test-class={TableTestClasses.MemberAvatar}
        >
          <MemberAvatar
            key={member.id}
            idMember={member.id}
            size={avatarSize}
            memberData={member}
            deactivated={member.deactivated}
          />
        </div>
      );
    });

    const isEmpty = memberIds.length === 0;
    const emptyReadOnly = isEmpty && !editable;

    return (
      <>
        <CellWrapper
          clickable={!emptyReadOnly ? true : false}
          isEmpty={isEmpty}
          onClick={!emptyReadOnly ? toggleMemberSelectPopover : undefined}
          ref={editable ? classicTriggerRef : triggerRef}
          testClass={TableTestClasses.MemberCell}
        >
          <div className={styles.membersCell}>{avatars}</div>
        </CellWrapper>

        <Popover {...popoverProps} title={viewTitle('members')}>
          <PopoverScreen id={Screen.MemberScreen}>
            <Facepile
              memberIds={memberIds}
              maxFaceCount={1600}
              showMore={false}
            />
          </PopoverScreen>
        </Popover>
      </>
    );
  },
);
